// src/components/pages/profile/organisms/ImageUploader.js

import React, { useState, useCallback } from "react";
import { Button, Box, Modal } from "@mui/material";
import styled from "@emotion/styled";
import Cropper from "react-easy-crop";
import { getCroppedImg } from "../../../utils/cropImage";
import { resizeImage } from "../../../utils/resizeImage";
import { uploadProfileImageToFirebase } from "../../../services/firebaseStorage";
import { useTheme } from "styled-components";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const ImageUploader = ({ onImageUpload, userId }) => {
  const theme = useTheme();
  const [preview, setPreview] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [open, setOpen] = useState(false);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      resizeImage(file, 550, 710, (resizedBlob) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
          setOpen(true);
        };
        reader.readAsDataURL(resizedBlob);
      });
    } else {
      setPreview(null);
    }
  };

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCrop = async () => {
    try {
      const croppedImg = await getCroppedImg(preview, croppedAreaPixels);
      const imageUrl = await uploadProfileImageToFirebase(croppedImg, userId); // Upload to Firebase
      onImageUpload(imageUrl); // Send the URL back to the parent component
      setOpen(false);
    } catch (error) {
      console.error("Error during image cropping or upload:", error);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Button
          component="label"
          color="primary"
          variant="outlined"
          sx={{
            border: "0px",
            fontSize: "1.1rem",
            color: "black",
            backgroundColor: "transparent",
            // padding: "10px 30px",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          }}
        >
          Edit Your Photo
          <VisuallyHiddenInput
            type="file"
            accept="image/*"
            onChange={handleFileChange}
          />
        </Button>
      </Box>
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            outline: "none",
          }}
        >
          <Cropper
            image={preview}
            crop={crop}
            zoom={zoom}
            aspect={1 / 1}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            style={{ containerStyle: { width: "100%", height: "100%" } }}
          />
          <Box
            sx={{ position: "fixed", bottom: "72px", display: "flex", gap: 2 }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleClose}
              sx={{
                backgroundColor: theme.colors.darkGray,
                fontWeight: "bold",
                color: theme.colors.white,
                textTransform: "none",
                borderRadius: "50px",
                padding: "5px 20px",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: theme.colors.lightGray,
                  color: theme.colors.darkGray,
                  boxShadow: "none",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCrop}
              sx={{
                backgroundColor: theme.colors.blue,
                fontWeight: "bold",
                color: theme.colors.white,
                textTransform: "none",
                borderRadius: "50px",
                padding: "5px 20px",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: theme.colors.green,
                  boxShadow: "none",
                },
              }}
            >
              Crop & Upload
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ImageUploader;